import { MainMenu } from './components/MainMenu';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import AppRoutes from './AppRoutes'; // Import the new AppRoutes component
import ChatWindow from './components/ChatWindow';
import { StyledButton } from './components/MasterProjectView';
import { useStore } from 'zustand';
import { useKeyBoardEventStore } from './stores/keyEventStore';
import chatBotImage from './images/ChatBot.webp';
import { useUnityBuildStore } from './stores/store';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppThemeProvider } from './theme/AppThemeProvider';
import { useGlobalSync } from './hooks/useGlobalSync';
import { AuthProvider } from './contexts/AuthContext';
import { useStopSyncOnAuthError } from './hooks/useStopSyncOnAuthError';
import { useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { OrientationProvider } from './theme/contexts';
import { HelmetProvider } from 'react-helmet-async';
import { useLocalStorage } from './stores/localStorage';
import { ToastProvider, ToastContainer } from './components/shared';
import { QueryClient, QueryClientProvider } from 'react-query';
import HelpDialog from './components/HelpDialog';

const oauthClientId = process.env.REACT_APP_OAUTH_CLIENT_ID!;

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Configuration for app-wide settings
const appConfig = {
  // Disable automatic sync and rely on manual refresh instead
  // This helps reduce performance issues in dual monitor setups
  disableAutoSync: true,
};

// Component to add global error handling
const GlobalErrorHandler = () => {
  const { logout } = useAuth();
  const setSessionToken = useStore(useLocalStorage).setSessionToken;

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      console.log('Unhandled rejection:', error);

      if (
        (error?.code === 16 && error?.message === 'authentication failed') ||
        (error?.code === 13 && error?.message === 'failed to authenticate user') ||
        (typeof error?.message === 'string' &&
          (error.message.includes('authentication failed') ||
            error.message.includes('failed to authenticate')))
      ) {
        console.error('AUTH ERROR captured in unhandled rejection:', error);
        handleAuthError(error);
      }
    };

    // Handle auth error event
    const handleAuthErrorEvent = (event: CustomEvent) => {
      console.error('AUTH ERROR event received:', event.detail);
      handleAuthError(event.detail);
    };

    // Show countdown dialog and logout
    const handleAuthError = (error: any) => {
      console.log('AUTH ERROR HANDLER CALLED with:', error);
      //clear the localStorage session token
      setSessionToken('');

      //Countdown duration to to return to the home screen
      let seconds = 5;

      // Create modal container (only if not already present)
      if (!document.getElementById('auth-error-modal')) {
        const modal = document.createElement('div');
        modal.id = 'auth-error-modal';
        modal.style.position = 'fixed';
        modal.style.zIndex = '9999';
        modal.style.left = '0';
        modal.style.top = '0';
        modal.style.width = '100%';
        modal.style.height = '100%';
        modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
        modal.style.display = 'flex';
        modal.style.alignItems = 'center';
        modal.style.justifyContent = 'center';

        // Create modal content
        const modalContent = document.createElement('div');
        modalContent.style.backgroundColor = 'white';
        modalContent.style.padding = '30px';
        modalContent.style.borderRadius = '5px';
        modalContent.style.width = '400px';
        modalContent.style.textAlign = 'center';
        modalContent.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';

        // Create title
        const title = document.createElement('h2');
        title.textContent = 'Session Expired';
        title.style.margin = '0 0 15px 0';
        title.style.color = '#333';

        // Create message
        const message = document.createElement('p');
        message.textContent = 'Your authentication session has expired. You will be logged out in:';
        message.style.margin = '0 0 20px 0';
        message.style.color = '#555';

        // Create countdown element
        const countdown = document.createElement('div');
        countdown.id = 'auth-error-countdown';
        countdown.textContent = `${seconds} seconds`;
        countdown.style.fontSize = '24px';
        countdown.style.fontWeight = 'bold';
        countdown.style.color = '#e74c3c';
        countdown.style.margin = '0 0 20px 0';

        // Add all elements to modal
        modalContent.appendChild(title);
        modalContent.appendChild(message);
        modalContent.appendChild(countdown);
        modal.appendChild(modalContent);

        // Add modal to body
        document.body.appendChild(modal);

        // Start countdown
        const interval = setInterval(() => {
          seconds--;

          const countdownEl = document.getElementById('auth-error-countdown');
          if (countdownEl) {
            countdownEl.textContent = `${seconds} seconds`;
          }

          if (seconds <= 0) {
            clearInterval(interval);
            const modalEl = document.getElementById('auth-error-modal');
            if (modalEl) {
              document.body.removeChild(modalEl);
            }
            logout();
          }
        }, 1000);
      }
    };

    // Add global listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('auth-error', handleAuthErrorEvent as EventListener);

    // Monitor for auth errors in API responses
    const originalFetch = window.fetch;
    window.fetch = async function (input, init) {
      try {
        const response = await originalFetch(input, init);

        // If response OK, return it
        if (response.ok) {
          return response;
        }

        // Check for auth errors in response
        const clone = response.clone();
        try {
          const data = await clone.json();
          // Safely check for authentication errors in the response
          const hasAuthError =
            typeof data === 'object' &&
            data !== null &&
            ((typeof data.code === 'number' && (data.code === 16 || data.code === 13)) ||
              (typeof data.message === 'string' &&
                (data.message.includes('authentication failed') ||
                  data.message.includes('failed to authenticate'))));

          if (hasAuthError) {
            console.error('AUTH ERROR captured in fetch response:', data);
            const authErrorEvent = new CustomEvent('auth-error', {
              detail: data,
            });
            window.dispatchEvent(authErrorEvent);
          }
        } catch (e) {
          // Ignore errors parsing response
        }

        return response;
      } catch (error: any) {
        // Check for auth errors in fetch exception
        const hasAuthError =
          error &&
          ((typeof error.code === 'number' && (error.code === 16 || error.code === 13)) ||
            (typeof error.message === 'string' &&
              (error.message.includes('authentication failed') ||
                error.message.includes('failed to authenticate'))));

        if (hasAuthError) {
          console.error('AUTH ERROR captured in fetch exception:', error);
          const authErrorEvent = new CustomEvent('auth-error', {
            detail: error,
          });
          window.dispatchEvent(authErrorEvent);
        }

        throw error;
      }
    };

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('auth-error', handleAuthErrorEvent as EventListener);
      window.fetch = originalFetch;
    };
  }, [logout, setSessionToken]);

  return null;
};

const AppContent = () => {
  const showChatWindow = useStore(useKeyBoardEventStore).showChatWindow;
  const setShowChatWindow = useStore(useKeyBoardEventStore).setShowChatWindow;
  const isLoggedIn = useStore(useUnityBuildStore).isLoggedIn;
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const location = useLocation();

  // Check if current path is a storefront path that should allow scrolling
  const isStorefrontPath = [
    '/',
    '/signup',
    '/terms',
    '/privacy',
    '/resources',
    '/knowledge',
    '/login',
    '/founders-statement',
    '/roadmap',
    '/checkout',
    '/return',
  ].some(
    path =>
      location.pathname === path ||
      location.pathname.startsWith('/signup/') ||
      location.pathname.startsWith('/knowledge/')
  );

  // Initialize global sync with auto sync disabled
  useGlobalSync(appConfig.disableAutoSync);

  // Monitor for authentication errors
  useStopSyncOnAuthError();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: isStorefrontPath ? 'auto' : 'hidden', // Allow scrolling for storefront pages
        fontFamily: "'Roboto', sans-serif",
        color: '#333',
      }}
    >
      {!isStorefrontPath && <MainMenu />}
      {/* Chat Window Icon */}
      {isLoggedIn && selectedProject.id && (
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            zIndex: 10000,
            padding: 2,
          }}
        >
          <StyledButton onClick={() => setShowChatWindow(!showChatWindow)}>
            <img
              src={chatBotImage}
              alt="Chat Window Icon"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '40%',
                objectFit: 'cover',
              }}
            />
          </StyledButton>
        </div>
      )}
      {showChatWindow && <ChatWindow />}
      <HelpDialog />
      <div style={{ flex: 1, overflow: isStorefrontPath ? 'auto' : 'hidden' }}>
        <AppRoutes />
      </div>
    </div>
  );
};

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={oauthClientId}>
        <Router>
          <ThemeProvider>
            <AppThemeProvider>
              <AuthProvider>
                <ToastProvider>
                  <GlobalErrorHandler />
                  <HelmetProvider>
                    <OrientationProvider>
                      <AppContent />
                      <ToastContainer />
                    </OrientationProvider>
                  </HelmetProvider>
                </ToastProvider>
              </AuthProvider>
            </AppThemeProvider>
          </ThemeProvider>
        </Router>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
};
